import React from "react";
import Select, { components, OptionProps, ValueContainerProps } from "react-select";
import { FilterCategory, SelectOption } from "./types";
import { swedishAriaLiveMessages } from "../SearchService/shared/swedishAriaLiveMessages";

export interface FilterSectionProps {
  category: FilterCategory;
  selectedValues: string[];
  isLoading: boolean;
  onChange: (category: FilterCategory, values: string[]) => void;
}

const SSR = typeof window === "undefined";

const withoutParentheticalText = (text: string) =>
  text.replace(/\s*\([^)]*\)/g, "").trim();

const CheckboxOption: React.FC<OptionProps<SelectOption, true>> = (props) => {
  const label = props.isSelected ? <b>{props.label}</b> : props.label;
  return (
    <div>
      <components.Option {...props}>
        <label>
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />{" "}
          {label}
        </label>
      </components.Option>
    </div>
  );
};

const toSentenceCase = (text: string) => {
  const [firstWord, ...rest] = text.trim().split(" ");
  return [firstWord, rest.join(" ").toLocaleLowerCase("sv-SE")].filter(Boolean).join(" ");
};

const FilterValueContainer: React.FC<ValueContainerProps<SelectOption, true>> = ({
  children,
  ...props
}) => {
  let [values, input] = children as [React.ReactNode, React.ReactNode];
  if (Array.isArray(values)) {
    const count = (values as React.ReactNode[]).length;
    values = <span>{props.selectProps.placeholder} (<b>{count}</b>)</span>;
  }
  return (
    <components.ValueContainer {...props}>
      {values}
      {input}
    </components.ValueContainer>
  );
};

export const FilterSection: React.FC<FilterSectionProps> = ({
  category,
  selectedValues,
  isLoading,
  onChange,
}) => {
  if (typeof window === "undefined") return null;

  const options: SelectOption[] = category.options.map((option) => ({
    value: option.value,
    label: withoutParentheticalText(option.text),
  }));

  const categoryLabel = toSentenceCase(category.label);
  const isMunicipalityFilter = categoryLabel.toLocaleLowerCase("sv-SE").includes("kommun");
  const visibleOptions = isMunicipalityFilter
    ? options.filter((option) => option.label !== "Alla kommuner")
    : options;
  const selectedOptions = options.filter((option) => selectedValues.includes(option.value));
 const customStyles = {
    placeholder: (base) => ({ ...base,  color: "#121212" }),
  }

  return (
    <div className="search-control-block">
      <fieldset>
        <legend className="sr-only">{categoryLabel}</legend>
        {!SSR ? (
          <Select
              aria-label={categoryLabel}
              isMulti={true}
              options={visibleOptions}
              value={selectedOptions}
              onChange={(values) => onChange(category, (values ?? []).map((o) => o.value))}
              placeholder={selectedValues.length === 0 ? `${categoryLabel} (Alla)` : categoryLabel}
              ariaLiveMessages={swedishAriaLiveMessages}
              isDisabled={isLoading}
              inputId={`project-search-filter-${category.label}`.replace(/[^a-zA-Z0-9_-]/g, "-")}
              instanceId={`project-search-filter-${category.label}`.replace(/[^a-zA-Z0-9_-]/g, "-")}
              components={{ Option: CheckboxOption, ValueContainer: FilterValueContainer }}
              styles={customStyles}
              className="multi-select"
              classNames={{
                control: (state) =>
                  state.isFocused
                    ? "multi-select__control multi-select__control--hide-input multi-select__control--focus"
                    : "multi-select__control multi-select__control--hide-input",
                valueContainer: () => "multi-select__value-container",
                menu: () => "multi-select__menu",
                input: () => "multi-select__input",
                menuList: () => "multi-select__menu-list multi-select__menu-list--border-radius",
                option: () => "multi-select__option multi-select__option--checkbox",
                indicatorsContainer: () => "multi-select__indicators-container",
                multiValue: () => "multi-select__multi-value",
                multiValueLabel: () => "multi-select__multi-value-label",
                multiValueRemove: (state) =>
                  state.isFocused
                    ? "multi-select__multi-value-remove multi-select__multi-value-remove--focus"
                    : "multi-select__multi-value-remove",
                clearIndicator: () => "multi-select__clear-indicator",
                placeholder: () => "multi-select__placeholder",
              }}
            />
          ) : null}
      </fieldset>
    </div>
  );
};
